import { useDeck } from "../../lib/deck-context";
import { fmtM, fmtPct } from "../../lib/model";
import { Card, Eyebrow, Note, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";

const Term = ({ label, value, accent, testid }) => (
  <div className="flex-1 min-w-[160px]">
    <div className={`font-mono text-[10px] tracking-[0.2em] uppercase ${accent ? "text-white/75" : "text-steel"}`}>{label}</div>
    <div data-testid={testid} className={`mt-3 font-display text-5xl lg:text-6xl tabular-nums leading-none ${accent ? "text-white" : "text-ink"}`}>{value}</div>
  </div>
);
const Op = ({ c }) => <div className="font-display text-5xl text-steel/60 px-2 self-center">{c}</div>;

export default function Valuation() {
  const { m, inputs } = useDeck();
  const caps = [7.5, 7.0, 6.75];
  const rents = Array.from(new Set([32, 34, 36, inputs.rent])).sort((a, b) => a - b);
  return (
    <Section testid="section-valuation">
      <SectionHeader num="08" eyebrow="How the Final Valuation Is Calculated" title="Income ÷ required return = value." status="preliminary" />
      <Reveal>
        <Card className="p-8 lg:p-12">
          <div className="flex flex-wrap items-stretch gap-6 lg:gap-8">
            <Term label="Net Operating Income" value={fmtM(m.noi, 3)} testid="valuation-noi" />
            <Op c="÷" />
            <Term label="Partner Base Capitalization Rate" value={fmtPct(inputs.cap)} testid="valuation-cap" />
            <Op c="=" />
            <div className="flex-[1.4] min-w-[240px] rounded-2xl bg-arq p-7">
              <Term label="Base Stabilized Value" value={`≈ ${fmtM(m.value)}`} accent testid="valuation-value" />
            </div>
          </div>
        </Card>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-6 mt-6">
        <Reveal delay={0.1} className="lg:col-span-5">
          <Card className="overflow-hidden h-full">
            <div className="px-7 pt-7 pb-4"><Eyebrow>Valuation Sensitivity · Current NOI</Eyebrow></div>
            <table data-testid="valuation-sensitivity-table" className="data-table w-full">
              <thead><tr><th>Exit Cap Rate</th><th className="!text-right">Stabilized Value</th></tr></thead>
              <tbody>
                {caps.map((c) => {
                  const base = c === 7.0;
                  return (
                    <tr key={c} data-testid={`sensitivity-row-${String(c).replace(".", "")}`} className={base ? "!bg-arq text-white hover:!bg-arq" : ""}>
                      <td className={`${base ? "!py-6 text-white" : "text-steel"}`}>
                        <span className={`tabular-nums ${base ? "font-display text-2xl" : ""}`}>{c.toFixed(2)}%</span>
                        {base && <span className="ml-3 font-mono text-[10px] tracking-[0.18em] text-white/75">PARTNER BASE</span>}
                      </td>
                      <td className={`text-right tabular-nums ${base ? "!py-6 font-display text-3xl" : "text-char"}`}>~{fmtM(m.valueAt(c))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <Card className="overflow-x-auto h-full">
            <div className="px-7 pt-7 pb-4 flex items-center justify-between"><Eyebrow>Rent × Exit Cap Rate</Eyebrow><span className="text-xs text-steel">{inputs.occ}% occ · {inputs.exp}% load</span></div>
            <table data-testid="valuation-rent-cap-grid" className="data-table w-full min-w-[480px]">
              <thead><tr><th>Weighted Avg Rent</th>{[6.75, 7.0, 7.5].map((c) => <th key={c} className={`!text-right ${c === 7.0 ? "!text-arq" : ""}`}>{c.toFixed(2)}% Cap</th>)}</tr></thead>
              <tbody>
                {rents.map((r) => (
                  <tr key={r}>
                    <td className={r === inputs.rent ? "text-ink font-medium" : "text-steel"}>${r}/SF{r === inputs.rent && <span className="ml-2 text-[10px] font-mono text-arq">CURRENT</span>}</td>
                    {[6.75, 7.0, 7.5].map((c) => {
                      const hit = c === 7.0 && r === inputs.rent;
                      return <td key={c} className={`text-right tabular-nums ${hit ? "!bg-arq text-white font-semibold" : c === 7.0 ? "bg-arq-soft text-ink" : "text-char"}`}>{fmtM(m.valueAt(c, m.noiAtRent(r)))}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Reveal>
      </div>
      <Note testid="valuation-not-appraisal" className="mt-6">
        <div className="mb-2"><StatusBadge s="preliminary" /></div>
        This is an income-based preliminary valuation, not a third-party appraisal. City ground rent is not yet in the base NOI and must be deducted dollar-for-dollar before capitalization.
      </Note>
    </Section>
  );
}
